import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { fn, array } from '@ember/helper';
import { or } from 'ember-truth-helpers';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import can from 'ember-can/helpers/can';
import set from 'ember-set-helper/helpers/set';
import {
  HdsPageHeader,
  HdsDropdown,
  HdsTextBody,
  HdsTextDisplay,
  HdsButton,
  HdsApplicationState,
} from '@hashicorp/design-system-components/components';
import { CoreLayoutMain, BreadcrumbsContainer, Flex } from 'core/components';
import { boxPadding } from 'core/modifiers';
import { rbacSortEffectiveRoles } from 'core/helpers';
import IamPolicy from 'core/utils/iam-policy';
import { supportTicketPrefillHref } from 'core/utils/support-ticket';
import RoleDefinitions from 'manage-access/components/manage-access/flyout/role-definitions';
import RolesTable from 'hcp/components/iam/users/roles-table';
import SummaryCard from 'hcp/components/iam/users/summary-card';
import TerraformPermissions from 'hcp/components/hcp/alert/terraform-permissions';
import Remove from 'hcp/components/page/users/remove';

/**
 * An object representing the current location of roles, with link and roles.
 * Required for the RoleDefinitionsFlyout.
 * @argument currentRolesLocation
 * @type {Object}
 */
/**
 * An object representing the current scope, with properties type, name, and id.
 * Required for the RoleDefinitionsFlyout.
 * @argument currentScopeLink
 * @type {Object}
 */
/**
 * The organization.
 * @argument organization
 * @type {Object}
 */
/**
 * The organization policy.
 * @argument organizationPolicy
 * @type {Object}
 */
/**
 * This should be the organization role with any project roles.
 * @argument roles
 * @type {Array}
 */
/**
 * The user principal.
 * @argument userPrincipal
 * @type {Object}
 */
/**
 * A flag for if scim is enabled.
 * @argument scimEnabled
 * @type {Object}
 */
/**
 *
 * `PageUsersDetail` renders the details of a user and allows editing
 * some user traits. Important: This component is only good for editing basic
 * roles and should not be used for any fine-grained context.
 *
 *
 * ```
 * <Page::Users::PageUsersDetail
 *   @currentRolesLocation={{@currentRolesLocation}}
 *   @currentScopeLink={{@currentScopeLink}}
 *   @organization={{this.model.organization}}
 *   @organizationPolicy={{this.model.policy}}
 *   @roles={{this.model.roles}}
 *   @userPrincipal={{this.model.userPrincipal}}
 *   @scimEnabled={{this.model.scimEnabled}}
 * />
 * ```
 *
 * @class PageUsersDetail
 *
 */
export default class PageUsersDetail extends Component {
  @service abilities;
  @service flashMessages;
  @service intl;
  @service router;

  @tracked showRemoveModal = false;
  @tracked usersToRemove = null;
  @tracked showRoleDefinitionsFlyout = false;

  get canDeleteUser() {
    const { userPrincipal } = this.args;
    return this.abilities.can('delete user', userPrincipal, {
      orgPolicy: this.OrganizationPolicy,
    });
  }

  get canUpdateUser() {
    return this.abilities.can('set-iam-policy in organization');
  }

  get canViewDropdown() {
    return this.canDeleteUser || this.canUpdateUser;
  }

  get isScimManaged() {
    const { userPrincipal, scimEnabled } = this.args;

    return scimEnabled && userPrincipal.scimSynchronized;
  }

  get shouldShowSeparator() {
    // only show the HDS separator if multiple menu items are present based on the user's abilities
    return (
      (this.canUpdateUser && this.isScimManaged) ||
      (this.canUpdateUser && this.canDeleteUser)
    );
  }

  get OrganizationPolicy() {
    const { organizationPolicy } = this.args;

    if (!organizationPolicy) {
      return undefined;
    }

    return new IamPolicy(organizationPolicy);
  }

  get organizationRoleId() {
    if (!this.OrganizationPolicy) {
      return undefined;
    }

    const { userPrincipal } = this.args;
    const { roleId } = this.OrganizationPolicy.getMemberById(userPrincipal.id);
    return roleId;
  }

  get roleLocations() {
    const { roleLocations = [] } = this.args;
    return roleLocations;
  }

  get supportTicketHref() {
    return supportTicketPrefillHref(this.userContext);
  }

  confirmRemoveUsers = (users) => {
    this.showRemoveModal = true;
    this.usersToRemove = users;
  };

  onRemoveUsersError = ({ users }) => {
    this.flashMessages.error(
      this.intl.t('components.page.access-control.users.remove.error.title', {
        numUsers: users.length,
      }),
      {
        actionText: this.intl.t(
          'components.page.access-control.users.remove.error.action'
        ),
        content: this.intl.t(
          'components.page.access-control.users.remove.error.content',
          {
            numUsers: users.length,
          }
        ),
        onAction: (close) => {
          window.location.href =
            'https://support.hashicorp.com/hc/en-us/requests/new';
          return close();
        },
      }
    );

    return;
  };

  onRemoveUsersSuccess = ({ users }) => {
    this.resetRemoveUsers();

    this.flashMessages.success(
      this.intl.t('components.page.access-control.users.remove.success', {
        numUsers: users.length,
      })
    );

    return this.router.transitionTo('cloud.access-control.users.list');
  };

  resetRemoveUsers = () => {
    this.showRemoveModal = false;
    this.usersToRemove = null;
  };

  <template>
    <CoreLayoutMain>
      <:header>
        <HdsPageHeader as |PH|>
          <PH.Breadcrumb>
            <BreadcrumbsContainer />
          </PH.Breadcrumb>
          <PH.IconTile @icon="user" />
          <PH.Title>
            {{or @userPrincipal.fullName @userPrincipal.email}}
          </PH.Title>
          <PH.Subtitle>
            {{t "components.page.access-control.users.detail.user-details"}}
          </PH.Subtitle>
          {{#let
            (can "ssoDelete user" @userPrincipal)
            as |shouldAllowSsoUserDeletion|
          }}
            {{#if this.canViewDropdown}}
              <PH.Actions>
                <HdsDropdown
                  @listPosition="bottom-right"
                  data-test-more-menu
                  as |dd|
                >
                  <dd.ToggleButton
                    @color="secondary"
                    @text={{t "components.form.manage"}}
                    data-test-user-detail-manage
                  />
                  {{#if this.canUpdateUser}}
                    <dd.Interactive
                      @icon="edit"
                      @model={{@userPrincipal.id}}
                      @route="cloud.access-control.role-assignments.detail.edit"
                      @text={{t
                        "components.page.access-control.users.detail.actions.edit-role-assignments"
                      }}
                      data-test-edit-assignment-button
                    />
                  {{/if}}
                  {{#if this.shouldShowSeparator}}
                    <dd.Separator />
                  {{/if}}
                  {{#if this.isScimManaged}}
                    <dd.Generic {{boxPadding "xs 0 xs md"}}>
                      <HdsTextBody
                        @color="primary"
                        @size="200"
                        data-test-scim-manage-user-text
                      >
                        {{t
                          "components.page.access-control.users.detail.actions.manage-scim-user"
                        }}
                      </HdsTextBody>
                    </dd.Generic>
                  {{else if this.canDeleteUser}}
                    <dd.Interactive
                      @color="critical"
                      @icon={{if
                        shouldAllowSsoUserDeletion
                        "trash"
                        "minus-circle"
                      }}
                      @text={{if
                        shouldAllowSsoUserDeletion
                        (t
                          "components.page.access-control.users.detail.actions.delete-user"
                        )
                        (t
                          "components.page.access-control.users.detail.actions.remove-user"
                        )
                      }}
                      {{on
                        "click"
                        (fn this.confirmRemoveUsers (array @userPrincipal))
                      }}
                      data-test-remove-user-button
                    />
                  {{/if}}
                </HdsDropdown>
              </PH.Actions>
            {{/if}}
          {{/let}}
        </HdsPageHeader>
      </:header>
      <:alert>
        <SummaryCard
          @scimEnabled={{@scimEnabled}}
          @ssoEnabled={{@ssoEnabled}}
          @userPrincipal={{@userPrincipal}}
        />
        <TerraformPermissions as |Controls|>
          <Controls.GoToTerraform />
        </TerraformPermissions>
      </:alert>
      <:body>
        <Flex @direction="column" @gap="sm" data-test-user-details>
          <HdsTextDisplay @tag="h2" @size="400">
            {{t "components.page.access-control.users.detail.roles"}}
          </HdsTextDisplay>
          {{#let this.roleLocations as |roleLocations|}}
            {{#if roleLocations.length}}
              <Flex @alignItems="center" @direction="row" @justifyContent="end">
                <HdsButton
                  @color="tertiary"
                  @icon="help"
                  @iconPosition="leading"
                  @isInline={{true}}
                  @text={{t
                    "components.page.access-control.users.detail.what-can-each-role-do"
                  }}
                  {{on "click" (set this "showRoleDefinitionsFlyout" true)}}
                  data-test-roles-chart-toggle-button
                />
              </Flex>
              <RolesTable
                @roleLocations={{rbacSortEffectiveRoles roleLocations}}
                data-test-user-detail-roles-table
              />
            {{else}}
              <HdsApplicationState
                {{boxPadding "2xl 0"}}
                data-test-user-detail-roles-table-application-state
                as |A|
              >
                <A.Header
                  @title={{t
                    "components.page.access-control.users.detail.user-has-no-roles"
                  }}
                />
                <A.Body
                  @text={{t
                    "components.page.access-control.users.detail.no-roles-might-be-intentional"
                  }}
                />
                <A.Footer as |F|>
                  <F.LinkStandalone
                    @icon="arrow-right"
                    @text={{t
                      "components.page.access-control.users.detail.go-to-projects"
                    }}
                    @route="cloud.orgs.detail.projects"
                    @models={{array @organization}}
                  />
                  <F.LinkStandalone
                    @icon="help"
                    @iconPosition="leading"
                    @text={{t
                      "components.page.access-control.users.detail.need-help"
                    }}
                    @href={{this.supportTicketHref}}
                  />
                </A.Footer>
              </HdsApplicationState>
            {{/if}}
          {{/let}}
        </Flex>
        <Remove
          @isActive={{this.showRemoveModal}}
          @organization={{@organization}}
          @onActiveChange={{this.resetRemoveUsers}}
          @onError={{this.onRemoveUsersError}}
          @onSuccess={{this.onRemoveUsersSuccess}}
          @users={{this.usersToRemove}}
        />
      </:body>
    </CoreLayoutMain>
    {{#if this.showRoleDefinitionsFlyout}}
      <RoleDefinitions
        @rolesLocation={{@currentRolesLocation}}
        @currentScopeLink={{@currentScopeLink}}
        @onClose={{set this "showRoleDefinitionsFlyout" false}}
      />
    {{/if}}
  </template>
}
